import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export type MobileMenuLink = { labelKey?: string; label?: string; path: string };

interface MobileMenuProps {
  shopLinks: MobileMenuLink[];
  brandLinks: MobileMenuLink[];
  t: (key: string) => string;
}

export default function MobileMenu({ shopLinks, brandLinks, t }: MobileMenuProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [openSection, setOpenSection] = useState<'shop' | 'brand' | null>(null);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 768) setIsOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname, searchParams]);

  const toggleSection = (section: 'shop' | 'brand') => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const linkLabel = (link: MobileMenuLink) =>
    link.labelKey ? t(link.labelKey) : (link.label ?? link.path);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Ouvrir le menu"
        className="flex h-10 w-10 items-center justify-center text-white/70 hover:text-white transition-colors md:hidden"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <Transition show={isOpen}>
        <Dialog onClose={() => setIsOpen(false)} className="relative z-[110]">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition-transform duration-300 ease-out"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition-transform duration-200 ease-in"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="fixed inset-y-0 left-0 w-72 bg-neutral-950 border-r border-white/10 flex flex-col">
              {/* Header du menu */}
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <Link to="/" onClick={() => setIsOpen(false)}>
                  <img
                    src="/assets/images/maisonadjiwhite.png"
                    alt="Maison Adji"
                    className="h-7 w-auto object-contain opacity-80"
                  />
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex h-9 w-9 items-center justify-center text-white/50 hover:text-white transition-colors"
                  aria-label="Fermer le menu"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* SHOP + THE BRAND en dropdown */}
              <nav className="flex-1 overflow-y-auto py-6 px-5">
                <ul className="flex flex-col gap-0">
                  {/* SHOP */}
                  <li className="border-b border-white/5">
                    <button
                      type="button"
                      onClick={() => toggleSection('shop')}
                      className="flex w-full items-center justify-between py-4 font-['Rubik'] text-sm tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors"
                    >
                      <span>{t('nav.shop')}</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-white/50 transition-transform duration-200 ${openSection === 'shop' ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {openSection === 'shop' && (
                      <ul className="pb-3 pl-0 flex flex-col gap-0">
                        {shopLinks.map((link) => (
                          <li key={link.path}>
                            <Link
                              to={link.path}
                              onClick={() => setIsOpen(false)}
                              className="block py-2.5 pl-0 font-['Rubik'] text-sm tracking-[0.1em] uppercase text-white/50 hover:text-white transition-colors"
                            >
                              {linkLabel(link)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>

                  {/* THE BRAND */}
                  <li className="border-b border-white/5">
                    <button
                      type="button"
                      onClick={() => toggleSection('brand')}
                      className="flex w-full items-center justify-between py-4 font-['Rubik'] text-sm tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors"
                    >
                      <span>{t('nav.the_brand')}</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 text-white/50 transition-transform duration-200 ${openSection === 'brand' ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {openSection === 'brand' && (
                      <ul className="pb-3 pl-0 flex flex-col gap-0">
                        {brandLinks.map((link) => (
                          <li key={link.path}>
                            <Link
                              to={link.path}
                              onClick={() => setIsOpen(false)}
                              className="block py-2.5 pl-0 font-['Rubik'] text-sm tracking-[0.1em] uppercase text-white/50 hover:text-white transition-colors"
                            >
                              {linkLabel(link)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                </ul>
              </nav>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
